import { TOS_ROLE_SCOPES, type TosRoleScope } from "./compliance-types";

/**
 * Pure helpers around TOS role scopes. The list itself comes from the
 * API (see `use-tos-role-scopes.ts`); these only order and label it so
 * every consumer renders the scopes the same way.
 */

type TranslateFn = (key: string, values?: Record<string, unknown>) => string;

const KNOWN_ORDER: ReadonlyMap<string, number> = new Map(
  TOS_ROLE_SCOPES.map((scope, index) => [scope, index]),
);

function isScopeKey(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function compareScopes(a: string, b: string): number {
  const rankA = KNOWN_ORDER.get(a) ?? KNOWN_ORDER.size;
  const rankB = KNOWN_ORDER.get(b) ?? KNOWN_ORDER.size;
  return rankA - rankB || a.localeCompare(b);
}

/**
 * Dedupes a list of scope keys and orders it: the known scopes first,
 * in their canonical order (GUEST, PROPERTY_OWNER, VENUE_OWNER, STAFF),
 * then anything else alphabetically.
 * Non-string / empty values are dropped — the API is the boundary.
 */
export function orderRoleScopes(scopes: Iterable<unknown>): readonly TosRoleScope[] {
  const unique = new Set(Array.from(scopes).filter(isScopeKey));
  return Array.from(unique).sort(compareScopes);
}

/** `PROPERTY_OWNER` → `Property Owner`. Label of last resort for a scope with no i18n key. */
export function humanizeRoleScope(scope: string): string {
  return scope
    .toLowerCase()
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * `t("compliance.roles.<SCOPE>")`, falling back to the humanized key
 * when the locale files don't know the scope yet — so a scope added
 * server-side renders readably before anyone adds its translation.
 */
export function roleScopeLabel(t: TranslateFn, scope: string): string {
  return t(`compliance.roles.${scope}`, { defaultValue: humanizeRoleScope(scope) });
}
